/**
 * When the mobile install banner is allowed on screen. Pure so it can be tested
 * in the plain-node unit project — the component around it is a client
 * component and the `unit` project has no DOM.
 */

export const DISMISSED_KEY = "ezspot:app-banner-dismissed-at";

/**
 * How long a dismissal sticks. Long enough that closing the banner reads as
 * "not interested" rather than "not right now" — the campaign re-asks after a
 * month, not on the next page view.
 */
export const DISMISSED_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** How far ahead of us a device's clock may be and still be believed. */
const CLOCK_SKEW_TOLERANCE_MS = 24 * 60 * 60 * 1000;

/**
 * Routes the banner stays off.
 *
 * `/search`, `/reserve` and `/messages` own the bottom of the mobile viewport
 * (results sheet, sticky action bar, message composer) and a fixed banner would
 * sit on top of all three. `/host` is excluded for two reasons: the listing and
 * availability wizards under it pin their own Back/Next row to the bottom, and
 * the host dashboard already carries `AppPromoPanel`, so a banner there asks the
 * same host to install twice on one screen. `/account` has the add-card and
 * add-vehicle sheets, which are themselves `fixed bottom-0 z-40` — the same
 * layer this banner uses, and it renders later, so it would win the tie and
 * cover their submit buttons.
 *
 * Related prior art worth keeping in sync by hand: the cookie banner keeps its
 * own exclusion list (`EXCLUDE_BANNER_ROUTES_REGEX` in
 * `~/components/cookieBanner/Banner`) for the same class of reason. The two
 * lists deliberately differ — that one has no `/search` or `/reserve` — so they
 * are not worth merging, but a new full-viewport route should be added to both.
 */
const HIDDEN_PREFIXES = [
  "/search",
  "/reserve",
  "/messages",
  "/host",
  "/account",
];

/** Mirrors the key `~/components/cookieBanner/Banner` writes on dismissal. */
const COOKIE_BANNER_DISMISSED_KEY = "ezspot_cookie_banner_dismissed";

/**
 * Whether a stored dismissal is still in effect. `stored` is the raw
 * localStorage value, so it can be absent or garbage (hand-edited, written by
 * an older build); anything unparseable reads as "never dismissed" and shows
 * the banner, which is the recoverable direction to fail.
 */
export function isWithinDismissalWindow(
  stored: string | null,
  now: number,
): boolean {
  const dismissedAt = Number(stored);

  // Every non-timestamp reads as "never dismissed", which shows the banner: the
  // recoverable direction to fail. `Number("1e999")` is Infinity, which is why
  // the finite check comes first.
  if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return false;

  // A future timestamp means the clock ran ahead when the banner was dismissed.
  // A small skew still counts as dismissed, because re-showing the banner to
  // someone who just closed it is the more annoying failure. An implausible one
  // does not: `now - dismissedAt` stays negative until real time catches up, so
  // a clock set years ahead would otherwise hide the banner for years.
  if (dismissedAt > now + CLOCK_SKEW_TOLERANCE_MS) return false;

  return now - dismissedAt < DISMISSED_WINDOW_MS;
}

/**
 * Whether the cookie banner is still on screen. It is `fixed bottom-0 z-50` and
 * mounts in the ROOT layout, after this banner's own subtree, so while it is up
 * it covers this banner completely and swallows its dismiss control — and the
 * visitor it covers is exactly the cold campaign traffic a QR or SMS delivers.
 * Wait for consent to be answered instead of stacking two bars.
 *
 * Reading the same key rather than sharing state keeps this a one-way
 * dependency. The trade is that accepting cookies does not re-render this
 * component, so the install banner appears on the next navigation rather than
 * popping in the instant the cookie bar leaves, which is the calmer behavior
 * anyway.
 */
export function isCookieBannerShowing(): boolean {
  try {
    return window.localStorage.getItem(COOKIE_BANNER_DISMISSED_KEY) !== "true";
  } catch {
    // Storage is blocked, so the cookie banner cannot record a dismissal and
    // stays up for this visitor. Suppress rather than render underneath it.
    return true;
  }
}

export function isBannerHiddenPath(pathname: string): boolean {
  return HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Reading `window.localStorage` is not safe to do bare: the property access
 * itself throws in Safari private mode and wherever site data is blocked, and
 * this banner mounts on every route in the group, so an uncaught throw would
 * take out the page rather than just the banner. Mirrors the guards in
 * `~/lib/events/last-vehicle-size`.
 */
export function readDismissedAt(): string | null {
  try {
    return window.localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

/** A write that fails should still dismiss the banner for this page view. */
export function writeDismissedAt(now: number): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(now));
  } catch {
    // Storage unavailable or full. The caller hides the banner regardless.
  }
}
